import DownloadIcon from '@mui/icons-material/Download';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterListIcon from '@mui/icons-material/FilterList';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { exportOperationalReportsToCSV } from '@sophocles/api/operational-reports';
import AlarmChip from '@sophocles/components/AlarmChip';
import RelativeDateRangePicker from '@sophocles/components/RelativeDateRangePicker';
import StatusChip from '@sophocles/components/StatusChip';
import { relayLoadSelector } from '@sophocles/entities/RelayLoad';
import { getAuthUserSelector } from '@sophocles/features/auth/authSelectors';
import { selectAlarmDefinitions } from '@sophocles/features/reference';
import {
  fetchOperationalReports,
  resetFilters,
  resetReports,
  selectAllReports,
  selectFilters,
  selectHasMorePages,
  selectPagination,
  selectReportsCount,
  selectReportsError,
  selectReportsLoading,
  selectSorting,
  setDateRangeFilter,
  setFilters,
  setLimit,
  setLoadIdFilter,
  setPage,
  setTextSearchFilter,
  setSiteNameFilter,
  setSortBy,
  setSortDesc,
  setStatusFilter,
} from '@sophocles/features/reports';
import {
  isScheduleComplianceReport,
  isPowerComplianceReport,
  isImageComplianceReport,
  getScheduleComplianceSummary,
  extractLoadDetails,
  formatScheduleTime,
} from '@sophocles/features/reports/utils/scheduleComplianceHelpers';
import { useAlarmTranslation } from '@sophocles/hooks/useAlarmTranslation';
import useRelayLoadTerm from '@sophocles/hooks/useRelayLoadTerm';
import { formatDate, formatTime } from '@sophocles/utils/formatUtils';

import ImageComplianceDetails from './ImageComplianceDetails';
import PowerComplianceDetails from './PowerComplianceDetails';
import ScheduleComplianceDetails from './ScheduleComplianceDetails';
import StatusBadge from './StatusBadge';

/**
 * OperationalReportsTable component displays a list of operational reports with filtering
 */
const OperationalReportsTable = ({ isStaff = false, hideSiteColumn = false, isBillboard = true }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const tAlarm = useAlarmTranslation();
  const allReports = useSelector(selectAllReports);
  const loading = useSelector(selectReportsLoading);
  const error = useSelector(selectReportsError);
  const count = useSelector(selectReportsCount);
  const hasMorePages = useSelector(selectHasMorePages);
  const filters = useSelector(selectFilters);
  const pagination = useSelector(selectPagination);
  const sorting = useSelector(selectSorting);
  const alarmDefinitions = useSelector(selectAlarmDefinitions);
  const user = useSelector(getAuthUserSelector);
  const relayLoads = useSelector(relayLoadSelector);

  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [loadingMore, setLoadingMore] = useState(false);
  const [viewMode, setViewMode] = useState('panel'); // 'date' or 'panel' - default to 'panel' (by load)

  // Ref for infinite scroll observer
  const loadMoreRef = React.useRef(null);

  // Filter reports based on staff status
  // Non-staff users cannot see pending reports
  const reports = isStaff
    ? allReports
    : allReports.filter(report => ['passed', 'failed', 'error', 'skipped'].includes(report.status?.toLowerCase()));

  // Status options - non-staff users only see passed/failed, staff see all
  const statusOptions = isStaff ? ['pending', 'passed', 'failed', 'error', 'skipped'] : ['passed', 'failed'];

  // Customer-type relay-load term for the panel/zone column + summary labels;
  // falls back to the billboard/commercial Panel/Zone defaults outside scope.
  const { singular: panelZoneLabel, plural: panelZoneLabelPlural } = useRelayLoadTerm(
    isBillboard
      ? { singularKey: 'reports.panel', pluralKey: 'reports.panels' }
      : { singularKey: 'reports.zone', pluralKey: 'reports.zones' },
  );

  const availablePanelsZones = useMemo(() => {
    // Convert Immutable Map to array of plain objects
    return relayLoads
      .valueSeq()
      .map(load => ({
        id: load.get('id'),
        name: load.get('name') || load.get('display_name') || `Load ${load.get('id')}`,
      }))
      .toArray()
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [relayLoads]);

  const loadReports = (resetPage = false) => {
    const params = {
      limit: pagination.limit,
      offset: resetPage ? 0 : pagination.offset,
    };

    // Add filters
    if (filters.site) params.site = filters.site;
    if (filters.site_name) params.site_name = filters.site_name;
    if (filters.status && filters.status.length > 0) {
      // Pass status as array - axios will serialize as status=value1&status=value2
      params.status = filters.status;
    }
    if (filters.validation_mode) params.validation_mode = filters.validation_mode;
    if (filters.report_date) params.report_date = filters.report_date;
    if (filters.date_range_start) params.date_range_start = filters.date_range_start;
    if (filters.date_range_end) params.date_range_end = filters.date_range_end;
    if (filters.text_search) {
      // Format text_search as: field1,field2|searchQuery
      params.text_search = `site__name,site__id|${filters.text_search}`;
    }
    if (filters.load_id) params.load_id = filters.load_id;

    // Add sorting
    const orderBy = sorting.sortDesc ? `-${sorting.sortBy}` : sorting.sortBy;
    params.o = orderBy;

    dispatch(fetchOperationalReports(params));
  };

  const loadMoreReports = async () => {
    if (loadingMore || !hasMorePages) return;

    setLoadingMore(true);
    const nextPage = pagination.page + 1;
    await dispatch(setPage(nextPage));
    setLoadingMore(false);
  };

  // Load reports on mount and when filters/sorting change
  useEffect(() => {
    // Reset to first page when filters or sorting change
    dispatch(resetReports());
    loadReports(true);
  }, [filters, sorting]);

  // Load more reports when pagination changes (infinite scroll)
  useEffect(() => {
    // Only load if offset > 0 (not initial load)
    if (pagination.offset > 0) {
      loadReports(false);
    }
  }, [pagination.offset]);

  // Infinite scroll observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        const target = entries[0];
        if (target.isIntersecting && hasMorePages && !loading && !loadingMore) {
          loadMoreReports();
        }
      },
      { threshold: 0.1 },
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => {
      if (loadMoreRef.current) {
        observer.unobserve(loadMoreRef.current);
      }
    };
  }, [hasMorePages, loading, loadingMore, pagination.offset]);

  const handleRefresh = () => {
    dispatch(resetReports());
    loadReports(true);
  };

  const handleExportToCSV = async () => {
    setExporting(true);
    try {
      const params = {};

      // Add filters (same as loadReports)
      if (filters.site) params.site = filters.site;
      if (filters.site_name) params.site_name = filters.site_name;
      if (filters.status && filters.status.length > 0) {
        params.status = filters.status;
      }
      if (filters.validation_mode) params.validation_mode = filters.validation_mode;
      if (filters.report_date) params.report_date = filters.report_date;
      if (filters.date_range_start) params.date_range_start = filters.date_range_start;
      if (filters.date_range_end) params.date_range_end = filters.date_range_end;
      if (filters.text_search) {
        params.text_search = `site__name,site__id|${filters.text_search}`;
      }
      if (filters.load_id) params.load_id = filters.load_id;

      // Add sorting
      const orderBy = sorting.sortDesc ? `-${sorting.sortBy}` : sorting.sortBy;
      params.o = orderBy;

      await exportOperationalReportsToCSV(params);
    } catch (err) {
      console.error('Error exporting to CSV:', err);
      // Could show error toast notification here
    } finally {
      setExporting(false);
    }
  };

  const handleResetFilters = () => {
    dispatch(resetFilters());
  };

  const handleSort = column => {
    if (sorting.sortBy === column) {
      dispatch(setSortDesc(!sorting.sortDesc));
    } else {
      dispatch(setSortBy(column));
      dispatch(setSortDesc(false));
    }
  };

  const handleRowClick = reportId => {
    // Clicking the row toggles expansion
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(reportId)) {
        newSet.delete(reportId);
      } else {
        newSet.add(reportId);
      }
      return newSet;
    });
  };

  const handleExpandClick = (reportId, event) => {
    event.stopPropagation();
    handleRowClick(reportId);
  };

  const handleStatusFilterChange = (event, newValue) => {
    dispatch(setStatusFilter(newValue));
  };

  const handleDateRangeChange = (startDate, endDate, rangeType, presetValue) => {
    const startStr = startDate ? startDate.toISOString().split('T')[0] : null;
    const endStr = endDate ? endDate.toISOString().split('T')[0] : null;
    dispatch(setDateRangeFilter({ start: startStr, end: endStr }));
  };

  // Get pass rate color similar to site group dashboard
  const getPassRateColor = passRate => {
    if (passRate === null || passRate === undefined) return 'text.secondary';
    if (passRate >= 90) return 'success.main';
    if (passRate >= 75) return 'warning.main';
    return 'error.main';
  };

  // Get smart summary for a report (returns JSX component for visual indicator)
  const getSummary = report => {
    const status = report.status?.toLowerCase();

    // Handle no_enabled_schedules case
    if (report.validation_reason === 'no_enabled_schedules') {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          {t('operationalReports.noEnabledSchedules', { defaultValue: 'No schedules enabled' })}
        </Typography>
      );
    }

    // For schedule_compliance reports, show pass/fail indicator with percentage
    if (isScheduleComplianceReport(report)) {
      const summary = getScheduleComplianceSummary(report);
      if (summary) {
        // Calculate by load/panel instead of by schedule
        const loadDetails = extractLoadDetails(report);

        // Filter to only enabled schedules
        const enabledSchedules = loadDetails.filter(schedule => schedule.enabled !== false);

        // If no enabled schedules, show a message
        if (enabledSchedules.length === 0) {
          return (
            <Typography variant="caption" color="text.secondary">
              {loadDetails.length === 0
                ? t('operationalReports.noSchedules', { defaultValue: 'No schedules' })
                : t('operationalReports.allSchedulesDisabled', { defaultValue: 'All schedules disabled' })}
            </Typography>
          );
        }

        // Group by load to get unique load counts (only for enabled schedules)
        const loadGroups = new Map();
        enabledSchedules.forEach(schedule => {
          const loadKey = schedule.loadIds.sort((a, b) => a - b).join(',');
          if (!loadGroups.has(loadKey)) {
            loadGroups.set(loadKey, {
              status: 'passed', // Will be set to failed if any schedule in this load fails
            });
          }
          // If any schedule in this load failed, mark the whole load as failed
          if (schedule.status === 'failed') {
            loadGroups.get(loadKey).status = 'failed';
          }
        });

        const totalLoads = loadGroups.size;
        const passedLoads = Array.from(loadGroups.values()).filter(load => load.status === 'passed').length;
        const passRate = totalLoads > 0 ? (passedLoads / totalLoads) * 100 : 0;
        const progressColor = passRate >= 90 ? 'success' : passRate >= 75 ? 'warning' : 'error';

        return (
          <Box display="flex" alignItems="center" gap={1.5} sx={{ minWidth: 100 }}>
            <Typography color={getPassRateColor(passRate)} variant="body2" fontWeight="medium" sx={{ minWidth: 40 }}>
              {passRate.toFixed(0)}%
            </Typography>
            <LinearProgress
              variant="determinate"
              value={passRate}
              color={progressColor}
              sx={{ flexGrow: 1, height: 6, borderRadius: 3, minWidth: 60 }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 35 }}>
              {passedLoads}/{totalLoads}
            </Typography>
          </Box>
        );
      }
    }

    // For all other reports, show simple pass/fail indicator
    let passRate = 0;
    let displayText = '';

    if (status === 'passed') {
      passRate = 100;
      displayText = '1/1';
    } else if (status === 'failed') {
      passRate = 0;
      displayText = '0/1';
    } else if (status === 'pending') {
      return t('operationalReports.statusPending');
    } else if (status === 'error' || status === 'skipped') {
      return status.charAt(0).toUpperCase() + status.slice(1);
    } else {
      return '-';
    }

    const progressColor = passRate >= 90 ? 'success' : 'error';

    return (
      <Box display="flex" alignItems="center" gap={1.5} sx={{ minWidth: 100 }}>
        <Typography color={getPassRateColor(passRate)} variant="body2" fontWeight="medium" sx={{ minWidth: 40 }}>
          {passRate.toFixed(0)}%
        </Typography>
        <LinearProgress
          variant="determinate"
          value={passRate}
          color={progressColor}
          sx={{ flexGrow: 1, height: 6, borderRadius: 3, minWidth: 60 }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 35 }}>
          {displayText}
        </Typography>
      </Box>
    );
  };

  // Get event chips for display (alarms + no checkin indicator)
  const getEventChips = report => {
    const chips = [];

    // Add "Missed Checkin" chip if applicable
    // Check validation_reason, data.no_checkin, OR communication_status
    const communicationStatus = report.data?.schedule_compliance?.communication_status;
    const missedCheckin =
      report.validation_reason === 'NO_CHECKIN_WITHIN_24H' ||
      report.data?.no_checkin ||
      (communicationStatus && !communicationStatus.status_received);

    if (missedCheckin) {
      const tooltipText = communicationStatus?.hours_since_last_status
        ? `No status received in ${Math.round(communicationStatus.hours_since_last_status)} hours`
        : 'No status received within 24 hours';

      chips.push(
        <Tooltip key="missed-checkin-tooltip" title={tooltipText} arrow>
          <StatusChip
            key="missed-checkin"
            label={t('operationalReports.missedCheckin', { defaultValue: 'Missed Checkin' })}
            color="warning"
          />
        </Tooltip>,
      );
    }

    // Add info code chips for schedule_compliance reports
    if (isScheduleComplianceReport(report) && report.items) {
      const infoCodes = new Set();

      // Collect all unique info codes from items
      const scheduleItems = report.items.filter(
        item => !item.validation_mode || item.validation_mode === 'schedule_compliance',
      );

      scheduleItems.forEach(item => {
        // Extract info codes from states array (new structure) or item level (fallback)
        const states = item.data?.states || [];

        // First, check states for info codes
        states.forEach(state => {
          const stateCodes = state.info_codes || (state.info_code ? [state.info_code] : []);
          const stateCodesArray = Array.isArray(stateCodes) ? stateCodes : [stateCodes];
          stateCodesArray.forEach(code => {
            if (code) {
              infoCodes.add(code);
            }
          });
        });

        // Fallback to item-level info codes (for backwards compatibility)
        const itemCodes = item.info_codes || (item.info_code ? [item.info_code] : []);
        const itemCodesArray = Array.isArray(itemCodes) ? itemCodes : [itemCodes];
        itemCodesArray.forEach(code => {
          if (code) {
            infoCodes.add(code);
          }
        });
      });

      // Add info code chips with tooltips (filter out NO_DEPLOYMENT_ATTEMPT and DEPLOYMENT_FAILED_BUT_CONFIG_MATCHES)
      infoCodes.forEach(infoCode => {
        // Skip codes that are not useful to display
        if (infoCode === 'NO_DEPLOYMENT_ATTEMPT' || infoCode === 'DEPLOYMENT_FAILED_BUT_CONFIG_MATCHES') {
          return;
        }

        const labelKey = `operationalReports.infoCode${infoCode}`;
        const descriptionKey = `operationalReports.infoCode${infoCode}_description`;

        const label = t(labelKey, { defaultValue: infoCode.replace(/_/g, ' ') });
        const description = t(descriptionKey, { defaultValue: '' });

        chips.push(
          <Tooltip key={`info-${infoCode}`} title={description || label} arrow>
            <Chip label={label} size="small" color="info" variant="outlined" sx={{ mr: 0.5, mb: 0.5 }} />
          </Tooltip>,
        );
      });
    }

    // Handle both old and new data structures for alarms
    // New structure: alarms in data.schedule_compliance.alarms_during_schedules
    // Old structure: alarms in data.alarms_during_schedules
    let alarmsData =
      report.data?.schedule_compliance?.alarms_during_schedules || report.data?.alarms_during_schedules || [];

    // For schedule_compliance reports, extract alarms from items (if not already in data)
    if (isScheduleComplianceReport(report) && report.items && alarmsData.length === 0) {
      const allAlarms = [];
      // Filter items to only schedule_compliance items
      const scheduleItems = report.items.filter(
        item => !item.validation_mode || item.validation_mode === 'schedule_compliance',
      );
      scheduleItems.forEach(item => {
        // New multi-state structure: alarms are in each state
        if (item.data?.states) {
          item.data.states.forEach(state => {
            if (state.alarms) {
              allAlarms.push(...state.alarms);
            }
          });
        }
        // Legacy structure: alarms_during_schedule at item level
        else if (item.data?.alarms_during_schedule) {
          allAlarms.push(...item.data.alarms_during_schedule);
        }
      });
      if (allAlarms.length > 0) {
        alarmsData = allAlarms;
      }
    }

    if (alarmsData.length === 0 && chips.length === 0) return null;

    // Get unique alarm keys - handle both old structure (alarm.key) and new structure (alarm_key)
    const uniqueAlarms = new Map();
    alarmsData.forEach(alarmData => {
      const alarmKey = alarmData.alarm?.key || alarmData.alarm_key;
      const alarmDisplayName = alarmData.alarm?.display_name || alarmData.alarm_display_name;
      // Backend uses fails_operational_report (true = failing, false = informational)
      const isFailing = alarmData.fails_operational_report !== false; // Default to failing if not specified
      const isInformational = !isFailing;

      if (alarmKey && !uniqueAlarms.has(alarmKey)) {
        uniqueAlarms.set(alarmKey, {
          key: alarmKey,
          display_name: alarmDisplayName,
          isInformational: isInformational,
        });
      }
    });

    // Add alarm chips
    const alarmChips = Array.from(uniqueAlarms.values()).map((alarm, idx) => {
      const alarmDetails = alarmDefinitions.find(a => a.key === alarm.key);
      const alarmName = alarmDetails
        ? tAlarm(`alarms.${alarm.key}`, {
            defaultValue: alarmDetails.display_name ?? (alarm.display_name || alarm.key),
          })
        : alarm.display_name || alarm.key;

      return (
        <AlarmChip
          key={`alarm-${idx}`}
          label={alarmName}
          color={alarm.isInformational ? 'info' : 'bad'}
          sx={{ mr: 0.5, mb: 0.5 }}
        />
      );
    });

    return [...chips, ...alarmChips];
  };

  const getFailureTypeDisplay = report => {
    const failureType = report.data?.failure_type;
    if (!failureType) return '-';

    switch (failureType) {
      case 'no_communication':
        return 'No Communication';
      case 'alarms_during_operation':
        return 'Alarms During Operation';
      default:
        return failureType;
    }
  };

  // Helper to generate filter summary text
  const getFilterSummary = () => {
    const parts = [];

    if (filters.text_search) {
      parts.push(
        t('operationalReports.filterSummarySearch', {
          query: filters.text_search,
          defaultValue: `Search: ${filters.text_search}`,
        }),
      );
    }

    if (filters.status && filters.status.length > 0) {
      const statusStr = filters.status
        .map(s => t(`operationalReports.status${s.charAt(0).toUpperCase()}${s.slice(1)}`))
        .join(', ');
      parts.push(
        t('operationalReports.filterSummaryStatus', { status: statusStr, defaultValue: `Status: ${statusStr}` }),
      );
    }

    if (filters.load_id) {
      // Find the load name from availablePanelsZones by ID
      const load = availablePanelsZones.find(pz => pz.id === filters.load_id);
      const loadName = load?.name || filters.load_id;
      parts.push(
        t('operationalReports.filterSummaryPanelZone', {
          panelZone: loadName,
          defaultValue: `${panelZoneLabel}: ${loadName}`,
        }),
      );
    }

    if (filters.date_range_start && filters.date_range_end) {
      const startStr = formatDate(filters.date_range_start, user);
      const endStr = formatDate(filters.date_range_end, user);
      parts.push(
        t('operationalReports.filterSummaryDateRange', {
          start: startStr,
          end: endStr,
          defaultValue: `${startStr} - ${endStr}`,
        }),
      );
    }

    if (parts.length === 0) {
      return t('operationalReports.noFiltersApplied', { defaultValue: 'No filters applied' });
    }

    return parts.join(' | ');
  };

  // Transform reports into panel-based rows for panel view
  const getPanelRows = () => {
    const panelRows = [];

    reports.forEach(report => {
      if (!isScheduleComplianceReport(report)) {
        // Non-schedule compliance reports show as-is
        panelRows.push({
          type: 'report',
          report,
          id: report.id,
        });
        return;
      }

      // Extract load details from schedule compliance report
      const loadDetails = extractLoadDetails(report);

      if (loadDetails.length === 0) {
        // No panels/loads, skip this report in panel view
        return;
      }

      // Group by load (panel/zone) - multiple schedules can belong to same load
      // Use sorted loadIds as key to ensure unique grouping by actual IDs
      const loadGroups = new Map();
      loadDetails.forEach(schedule => {
        const loadKey = schedule.loadIds.sort((a, b) => a - b).join(',');
        if (!loadGroups.has(loadKey)) {
          loadGroups.set(loadKey, {
            loadNames: schedule.loadNames,
            loadIds: schedule.loadIds,
            schedules: [],
          });
        }
        loadGroups.get(loadKey).schedules.push(schedule);
      });

      // Create a row for each panel/load group
      loadGroups.forEach((loadGroup, loadKey) => {
        // Filter out loads if a load filter is active
        if (filters.load_id && !loadGroup.loadIds.includes(parseInt(filters.load_id))) {
          return; // Skip this load group if it doesn't match the filter
        }

        // Determine overall status for this panel/load
        const hasFailedSchedule = loadGroup.schedules.some(s => s.status === 'failed');
        const panelStatus = hasFailedSchedule ? 'failed' : 'passed';

        // Count alarms for this panel/load
        let totalAlarms = 0;
        let failingAlarms = 0;
        let informationalAlarms = 0;
        const uniqueAlarmKeys = new Set();

        loadGroup.schedules.forEach(schedule => {
          if (schedule.alarms) {
            schedule.alarms.forEach(alarm => {
              const alarmKey = alarm.alarm_key || alarm.alarm?.key;
              if (alarmKey && !uniqueAlarmKeys.has(alarmKey)) {
                uniqueAlarmKeys.add(alarmKey);
                totalAlarms++;
                const isFailing = alarm.fails_operational_report !== false;
                if (isFailing) {
                  failingAlarms++;
                } else {
                  informationalAlarms++;
                }
              }
            });
          }
        });

        panelRows.push({
          type: 'panel',
          report,
          loadGroup,
          panelStatus,
          alarmCount: totalAlarms,
          failingAlarmCount: failingAlarms,
          informationalAlarmCount: informationalAlarms,
          id: `${report.id}-${loadKey}`,
        });
      });
    });

    return panelRows;
  };

  return (
    <Box>
      {/* Filters Section */}
      <Accordion expanded={filtersExpanded} onChange={(e, isExpanded) => setFiltersExpanded(isExpanded)} sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box display="flex" alignItems="center" gap={1} width="100%">
            <FilterListIcon />
            <Typography variant="h6">{t('operationalReports.filters', { defaultValue: 'Filters' })}</Typography>
            {!filtersExpanded && (
              <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                {getFilterSummary()}
              </Typography>
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box display="flex" alignItems="center" justifyContent="flex-end" mb={2}>
            <Button size="small" onClick={handleResetFilters}>
              {t('operationalReports.resetFilters', { defaultValue: 'Reset Filters' })}
            </Button>
          </Box>
          <Grid container spacing={2}>
            {/* Search */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                label={t('operationalReports.search', { defaultValue: 'Search' })}
                value={filters.text_search || ''}
                onChange={e => dispatch(setTextSearchFilter(e.target.value))}
                placeholder={t('operationalReports.searchBySiteName', { defaultValue: 'Search by site name...' })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Status Filter */}
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <Autocomplete
                size="small"
                multiple
                options={statusOptions}
                value={filters.status}
                onChange={handleStatusFilterChange}
                getOptionLabel={option =>
                  t(`operationalReports.status${option.charAt(0).toUpperCase()}${option.slice(1)}`)
                }
                renderInput={params => <TextField {...params} label={t('operationalReports.status')} />}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      key={option}
                      label={t(`operationalReports.status${option.charAt(0).toUpperCase()}${option.slice(1)}`)}
                      size="small"
                      {...getTagProps({ index })}
                    />
                  ))
                }
              />
            </Grid>

            {/* Panel/Zone Filter */}
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <Autocomplete
                size="small"
                options={availablePanelsZones}
                value={availablePanelsZones.find(load => load.id === filters.load_id) || null}
                onChange={(e, newValue) => dispatch(setLoadIdFilter(newValue?.id || null))}
                getOptionLabel={option => option.name || ''}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                renderInput={params => <TextField {...params} label={panelZoneLabel} />}
              />
            </Grid>

            {/* Date Range */}
            <Grid size={{ xs: 12, sm: 6, md: 5 }}>
              <RelativeDateRangePicker
                startDate={filters.date_range_start ? new Date(filters.date_range_start) : null}
                endDate={filters.date_range_end ? new Date(filters.date_range_end) : null}
                onChange={handleDateRangeChange}
                label={t('operationalReports.dateRange', { defaultValue: 'Date Range' })}
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Action Bar */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h6">
          {t('operationalReports.operationalReports', { defaultValue: 'Operational Reports' })}
        </Typography>
        <Box display="flex" gap={1} alignItems="center">
          {/* View Toggle */}
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, newView) => {
              if (newView !== null) {
                setViewMode(newView);
              }
            }}
            size="small"
          >
            <ToggleButton value="date">
              <Tooltip title={t('operationalReports.dateGroupedView', { defaultValue: 'Group by Date' })}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <ViewListIcon fontSize="small" />
                  <Typography variant="caption">
                    {t('operationalReports.byDate', { defaultValue: 'By Date' })}
                  </Typography>
                </Box>
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="panel">
              <Tooltip
                title={t('operationalReports.panelGroupedView', {
                  term: panelZoneLabel,
                  defaultValue: 'Group by {{term}}',
                })}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <ViewModuleIcon fontSize="small" />
                  <Typography variant="caption">
                    {t('operationalReports.byPanel', { term: panelZoneLabel, defaultValue: 'By {{term}}' })}
                  </Typography>
                </Box>
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>

          <Tooltip title={t('operationalReports.exportToCSV', { defaultValue: 'Export to CSV' })}>
            <span>
              <IconButton onClick={handleExportToCSV} disabled={exporting || loading}>
                <DownloadIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={t('operationalReports.refresh', { defaultValue: 'Refresh' })}>
            <IconButton onClick={handleRefresh}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Error Display */}
      {error && (
        <Paper sx={{ p: 2, mb: 2, bgcolor: 'error.light', color: 'error.dark' }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            {t('operationalReports.errorLoadingReports', { defaultValue: 'Error loading reports' })}
          </Typography>
          <Typography variant="body2">{error}</Typography>
        </Paper>
      )}

      {/* Table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              <TableCell>
                <TableSortLabel
                  active={sorting.sortBy === 'report_date'}
                  direction={sorting.sortDesc ? 'desc' : 'asc'}
                  onClick={() => handleSort('report_date')}
                >
                  {t('operationalReports.reportDate')}
                </TableSortLabel>
              </TableCell>
              {!hideSiteColumn && (
                <TableCell>
                  <TableSortLabel
                    active={sorting.sortBy === 'site__name'}
                    direction={sorting.sortDesc ? 'desc' : 'asc'}
                    onClick={() => handleSort('site__name')}
                  >
                    {t('operationalReports.siteName')}
                  </TableSortLabel>
                </TableCell>
              )}
              {viewMode === 'panel' && <TableCell>{panelZoneLabel}</TableCell>}
              <TableCell align={viewMode === 'date' ? 'center' : 'left'}>
                {t('operationalReports.summary', { defaultValue: 'Summary' })}
              </TableCell>
              <TableCell>{t('operationalReports.events', { defaultValue: 'Events' })}</TableCell>
              <TableCell>{t('operationalReports.status')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && pagination.offset === 0 && reports.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={viewMode === 'panel' ? (hideSiteColumn ? 6 : 7) : hideSiteColumn ? 5 : 6}
                  align="center"
                  sx={{ py: 4 }}
                >
                  <Typography>{t('operationalReports.loading')}</Typography>
                </TableCell>
              </TableRow>
            )}
            {!loading && reports.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={viewMode === 'panel' ? (hideSiteColumn ? 6 : 7) : hideSiteColumn ? 5 : 6}
                  align="center"
                  sx={{ py: 4 }}
                >
                  <Typography color="text.secondary">{t('operationalReports.noReports')}</Typography>
                </TableCell>
              </TableRow>
            )}

            {/* Date View - Group by date (current view) */}
            {viewMode === 'date' &&
              reports.map(report => {
                const isExpanded = expandedRows.has(report.id);
                const canExpand = isScheduleComplianceReport(report);

                return (
                  <React.Fragment key={report.id}>
                    <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => handleRowClick(report.id)}>
                      <TableCell padding="checkbox">
                        {canExpand && (
                          <IconButton
                            className="expand-button"
                            size="small"
                            onClick={e => handleExpandClick(report.id, e)}
                            aria-label="expand row"
                          >
                            {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                          </IconButton>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1" fontWeight="medium">
                          {report.report_date}
                        </Typography>
                      </TableCell>
                      {!hideSiteColumn && (
                        <TableCell>
                          <Typography variant="body2">{report.site?.name || `Site #${report.site?.id}`}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            ID: {report.site?.id}
                          </Typography>
                        </TableCell>
                      )}
                      <TableCell align="center">
                        {typeof getSummary(report) === 'string' ? (
                          <Typography variant="body2">{getSummary(report)}</Typography>
                        ) : (
                          getSummary(report)
                        )}
                      </TableCell>
                      <TableCell>
                        <Box display="flex" flexWrap="wrap" gap={0.5}>
                          {getEventChips(report)}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={report.status} size="small" />
                      </TableCell>
                    </TableRow>
                    {canExpand && (
                      <TableRow>
                        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={hideSiteColumn ? 5 : 6}>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            {/* Render all validation mode sections that exist in this report */}
                            {isScheduleComplianceReport(report) && (
                              <ScheduleComplianceDetails
                                report={{
                                  ...report,
                                  // Filter items to only include schedule_compliance items
                                  items: report.items?.filter(
                                    item => !item.validation_mode || item.validation_mode === 'schedule_compliance',
                                  ),
                                }}
                                isStaff={isStaff}
                                isBillboard={isBillboard}
                              />
                            )}
                            {isPowerComplianceReport(report) && <PowerComplianceDetails report={report} />}
                            {isImageComplianceReport(report) && <ImageComplianceDetails report={report} />}
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}

            {/* Panel View - One row per panel per date */}
            {viewMode === 'panel' &&
              getPanelRows().map(panelRow => {
                if (panelRow.type === 'report') {
                  // Non-schedule compliance report or report with no panels
                  const report = panelRow.report;
                  const isExpanded = expandedRows.has(report.id);
                  const canExpand = isScheduleComplianceReport(report);

                  return (
                    <React.Fragment key={panelRow.id}>
                      <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => handleRowClick(report.id)}>
                        <TableCell padding="checkbox">
                          {canExpand && (
                            <IconButton
                              className="expand-button"
                              size="small"
                              onClick={e => handleExpandClick(report.id, e)}
                              aria-label="expand row"
                            >
                              {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                            </IconButton>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body1" fontWeight="medium">
                            {report.report_date}
                          </Typography>
                        </TableCell>
                        {!hideSiteColumn && (
                          <TableCell>
                            <Typography variant="body2">{report.site?.name || `Site #${report.site?.id}`}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              ID: {report.site?.id}
                            </Typography>
                          </TableCell>
                        )}
                        <TableCell>-</TableCell>
                        <TableCell align="left">
                          {typeof getSummary(report) === 'string' ? (
                            <Typography variant="body2">{getSummary(report)}</Typography>
                          ) : (
                            getSummary(report)
                          )}
                        </TableCell>
                        <TableCell>
                          <Box display="flex" flexWrap="wrap" gap={0.5}>
                            {getEventChips(report)}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={report.status} size="small" />
                        </TableCell>
                      </TableRow>
                      {canExpand && (
                        <TableRow>
                          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={hideSiteColumn ? 6 : 7}>
                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                              {/* Panel view - render all validation mode sections */}
                              {isScheduleComplianceReport(panelRow.report) && (
                                <ScheduleComplianceDetails
                                  report={{
                                    ...panelRow.report,
                                    items: panelRow.report.items?.filter(
                                      item => !item.validation_mode || item.validation_mode === 'schedule_compliance',
                                    ),
                                  }}
                                  isStaff={isStaff}
                                  isBillboard={isBillboard}
                                />
                              )}
                              {isPowerComplianceReport(panelRow.report) && (
                                <PowerComplianceDetails report={panelRow.report} />
                              )}
                              {isImageComplianceReport(panelRow.report) && (
                                <ImageComplianceDetails report={panelRow.report} />
                              )}
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                }

                // Panel row
                const report = panelRow.report;
                const loadGroup = panelRow.loadGroup;
                const isExpanded = expandedRows.has(panelRow.id);

                // Create summary for this panel - comma-delimited schedule times
                // Show actual sunset/sunrise times (not abbreviations)
                const formatScheduleTimeForPanel = (time, type, offset) => {
                  if (!time) return '';

                  // For sunrise/sunset, just show the actual time (not the label)
                  if (type === 'sunrise' || type === 'sunset') {
                    // Extract and format just the time portion
                    let cleanTime = time;
                    if (time.includes('T')) {
                      cleanTime = time.split('T')[1]?.split('.')[0] || time;
                    } else if (time.includes('.')) {
                      cleanTime = time.split('.')[0];
                    }
                    return formatTime(cleanTime, user);
                  }

                  // For fixed times, use the proper locale-aware formatter
                  return formatScheduleTime(time, type, offset, user, t);
                };

                // Filter schedules for display
                const enabledSchedulesForDisplay = loadGroup.schedules.filter(
                  schedule =>
                    schedule.enabled !== false && !schedule.infoCodes?.includes('SCHEDULE_NOT_ACTIVE_THIS_DAY'),
                );

                // Show message if no enabled schedules
                let scheduleSummaries;
                if (enabledSchedulesForDisplay.length === 0) {
                  scheduleSummaries = (
                    <Typography variant="caption" color="text.secondary">
                      {loadGroup.schedules.length === 0
                        ? t('operationalReports.noSchedules', { defaultValue: 'No schedules' })
                        : t('operationalReports.allSchedulesDisabled', { defaultValue: 'All schedules disabled' })}
                    </Typography>
                  );
                } else {
                  scheduleSummaries = enabledSchedulesForDisplay.map((schedule, idx, filteredSchedules) => {
                    // Check if schedule is "always on"
                    const isAlwaysOn =
                      (!schedule.onTime && !schedule.offTime) ||
                      (schedule.onTime === '00:00:00' && schedule.offTime === '00:00:00') ||
                      (schedule.onTime === null && schedule.offTime === null);

                    if (isAlwaysOn) {
                      return (
                        <Box key={idx} component="span" sx={{ whiteSpace: 'nowrap' }}>
                          {t('operationalReports.alwaysOn', { defaultValue: 'Always On' })}
                          {idx < filteredSchedules.length - 1 && ', '}
                        </Box>
                      );
                    }

                    const startTime = formatScheduleTimeForPanel(schedule.onTime, schedule.onType, schedule.onOffset);
                    const endTime = formatScheduleTimeForPanel(schedule.offTime, schedule.offType, schedule.offOffset);
                    return (
                      <Box key={idx} component="span" sx={{ whiteSpace: 'nowrap' }}>
                        {startTime} - {endTime}
                        {idx < filteredSchedules.length - 1 && ', '}
                      </Box>
                    );
                  });
                }

                // Create a mock report object with just this panel's data for getEventChips
                const panelReport = {
                  ...report,
                  validation_reason:
                    panelRow.panelStatus === 'failed' ? 'schedules_had_alarms' : report.validation_reason,
                  items: report.items?.filter(item => {
                    const loads = item.metadata?.loads || [];
                    const itemLoadIds = loads.map(l => l.id);
                    return loadGroup.loadIds.some(id => itemLoadIds.includes(id));
                  }),
                };

                return (
                  <React.Fragment key={panelRow.id}>
                    <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => handleRowClick(panelRow.id)}>
                      <TableCell padding="checkbox">
                        <IconButton
                          className="expand-button"
                          size="small"
                          onClick={e => handleExpandClick(panelRow.id, e)}
                          aria-label="expand row"
                        >
                          {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1" fontWeight="medium">
                          {report.report_date}
                        </Typography>
                      </TableCell>
                      {!hideSiteColumn && (
                        <TableCell>
                          <Typography variant="body2">{report.site?.name || `Site #${report.site?.id}`}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            ID: {report.site?.id}
                          </Typography>
                        </TableCell>
                      )}
                      <TableCell>
                        <Typography variant="body1" fontWeight="medium">
                          {loadGroup.loadNames}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="div" variant="caption">
                          {scheduleSummaries}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" flexWrap="wrap" gap={0.5}>
                          {getEventChips(panelReport)}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={panelRow.panelStatus} size="small" />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={hideSiteColumn ? 6 : 7}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <ScheduleComplianceDetails
                            report={{
                              ...report,
                              // Filter items to only include this specific panel/load AND schedule_compliance mode
                              items: report.items?.filter(item => {
                                // Filter by validation_mode
                                const isScheduleMode =
                                  !item.validation_mode || item.validation_mode === 'schedule_compliance';
                                if (!isScheduleMode) return false;

                                // Filter by load ID
                                const loads = item.metadata?.loads || [];
                                const itemLoadIds = loads.map(l => l.id);
                                return loadGroup.loadIds.some(id => itemLoadIds.includes(id));
                              }),
                            }}
                            hideStats={true}
                            hidePanelTitle={true}
                            isStaff={isStaff}
                            isBillboard={isBillboard}
                          />
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
          </TableBody>
        </Table>

        {/* Infinite scroll trigger */}
        {hasMorePages && (
          <Box ref={loadMoreRef} sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {loadingMore ? t('operationalReports.loadingMore', { defaultValue: 'Loading more...' }) : ''}
            </Typography>
          </Box>
        )}

        {/* End of results indicator */}
        {!hasMorePages && reports.length > 0 && (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {t('operationalReports.endOfResults', {
                count: reports.length,
                defaultValue: `Showing all ${reports.length} results`,
              })}
            </Typography>
          </Box>
        )}
      </TableContainer>
    </Box>
  );
};

export default OperationalReportsTable;
